/**
 * Frontend API client for the Signals tab.
 * Maps 1:1 to /v1/observe/dbview/signals/* in
 * backend/product/src/api/server.ts.
 */

import { request } from './client';

// ── Types ───────────────────────────────────────────────────────────────────

export type SignalKind = 'hot' | 'meh' | 'weak';
export type SignalDim  = 'category' | 'source' | 'dealTier';

export interface SignalFiltersInput {
  category?:  string;
  source?:    string;
  dealTier?:  'exceptional' | 'strong' | 'standard';
  modelKey?:  string;
  kind?:      SignalKind | 'all';
}

export interface SignalsHeadline {
  ok: true;
  sinceMs: number;
  hotCount: number;
  mehCount: number;
  weakCount: number;
  responseRate: number | null;
  hotToWeakRatio: number | null;
  distinctUsers: number;
  topModelKey: { modelKey: string; count: number } | null;
}

export interface SignalsTimeSeriesBucket {
  ts: string;
  hot:  number;
  meh:  number;
  weak: number;
  deliveries: number;
}

export interface SignalsBreakdownRow {
  key: string;
  hot:  number;
  meh:  number;
  weak: number;
  total: number;
}

export interface TopModelKeyRow {
  modelKey: string;
  hot:  number;
  meh:  number;
  weak: number;
  hotWeakRatio: number | null;
}

export interface UserConcentrationRow {
  tgId: number;
  hot:  number;
  meh:  number;
  weak: number;
  total: number;
  lastEventAt: string | null;
}

export interface HotWeakRatioRow {
  modelKey: string;
  hot:  number;
  weak: number;
  ratio: number;
  volume: number;
}

export interface ClickPairingRow {
  modelKey: string;
  hotCount: number;
  hotWithClick: number;
  ratio: number;
}

export interface TimeOfDayBucket {
  hour: number;
  hot:  number;
  meh:  number;
  weak: number;
  total: number;
}

// ── Fetchers ────────────────────────────────────────────────────────────────

function qs(params: object): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export function getSignalsHeadline(opts: { sinceMs?: number } & SignalFiltersInput) {
  return request<SignalsHeadline>(`/v1/observe/dbview/signals/headline${qs(opts)}`);
}

export function getSignalsTimeseries(opts: { sinceMs?: number; bucket?: 'day' | 'week' } & SignalFiltersInput) {
  return request<{ ok: true; rows: SignalsTimeSeriesBucket[] }>(
    `/v1/observe/dbview/signals/timeseries${qs(opts)}`,
  );
}

export function getSignalsBreakdown(opts: { dim: SignalDim; sinceMs?: number } & SignalFiltersInput) {
  return request<{ ok: true; rows: SignalsBreakdownRow[] }>(
    `/v1/observe/dbview/signals/breakdown${qs(opts)}`,
  );
}

export function getTopModelKeys(opts: { kind: SignalKind; sinceMs?: number; limit?: number } & SignalFiltersInput) {
  return request<{ ok: true; rows: TopModelKeyRow[] }>(
    `/v1/observe/dbview/signals/top-modelkeys${qs(opts)}`,
  );
}

export function getUserConcentration(opts: { sinceMs?: number; limit?: number } & SignalFiltersInput) {
  return request<{ ok: true; rows: UserConcentrationRow[] }>(
    `/v1/observe/dbview/signals/user-concentration${qs(opts)}`,
  );
}

export function getHotWeakRatio(opts: { sinceMs?: number; minVolume?: number; limit?: number } & SignalFiltersInput) {
  return request<{ ok: true; rows: HotWeakRatioRow[] }>(
    `/v1/observe/dbview/signals/ratio${qs(opts)}`,
  );
}

export function getClickPairing(opts: { sinceMs?: number; limit?: number } & SignalFiltersInput) {
  return request<{ ok: true; rows: ClickPairingRow[] }>(
    `/v1/observe/dbview/signals/click-pairing${qs(opts)}`,
  );
}

export function getTimeOfDay(opts: { sinceMs?: number } & SignalFiltersInput) {
  return request<{ ok: true; rows: TimeOfDayBucket[] }>(
    `/v1/observe/dbview/signals/time-of-day${qs(opts)}`,
  );
}

export function signalsExportPath(opts: {
  section: 'timeseries' | 'breakdown' | 'top-hot' | 'top-weak' | 'user-concentration' | 'ratio';
  sinceMs?: number;
  bucket?: 'day' | 'week';
  dim?: SignalDim;
} & SignalFiltersInput): string {
  return `/v1/observe/dbview/signals/export${qs(opts)}`;
}
